import { supabase } from '../../lib/supabase';
import { ENV } from '../../config/env';

export interface PipelineStageRow { stage: string; count: number; amount: number }
export interface WinRate90d { won: number; lost: number; total: number; rate: number }
export interface StageVelocityRow { stage: string; avgDays: number }
export interface ActivityVolumeRow { user: string; total: number; byType: Record<string, number> }

export interface DashboardSummary {
  pipelineByStage: PipelineStageRow[];
  winRate90d: WinRate90d;
  stageVelocityOpen: StageVelocityRow[];
  activityVolume30d: ActivityVolumeRow[];
  kpis: {
    openPipelineValue: number;
    openDealsCount: number;
    tasksDueNext24h: number;
    meetingsThisWeek: number;
    emailsThisWeek: number;
    jobs24hSuccess?: number;
    jobs24hFailed?: number;
  };
  heroMetrics?: {
    totalContacts: number;
    activeCompanies: number;
    openDealsValue: number;
    thisMonthDeals: number;
    conversionRate: number | null;
  };
  series?: {
    tasksNext7DaysByDay: Array<{ date: string; count: number }>;
    meetingsThisWeekByDay: Array<{ date: string; count: number }>;
  };
  sprint3?: {
    conversionFunnel: Array<{ stage: string; count: number }>;
    repVelocityDaysToWin: Array<{ user: string; avgDays: number; wins: number }>;
    jobs24h?: Array<{ id: string; status: string; created_at: string }>;
  };
  forecast?: {
    weightedPipeline: number;
    bestCase: number;
    commit: number;
    quotaTarget?: number;
    coverageRatio?: number;
    nextQuarterProjection?: number;
    forecastByOwner?: Array<{
      owner: string;
      weighted: number;
      commit: number;
    }>;
    cashflowSeries?: Array<{ date: string; amount: number }>;
  };
}

export async function fetchDashboardSummary(
  organization_id: string,
  opts?: { rangeDays?: 7 | 30 | 90; onlyMine?: boolean }
): Promise<DashboardSummary> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const userId = data.session?.user?.id;
  if (!token) throw new Error('Not authenticated');
  const url = new URL('/api/crm/dashboard/summary', ENV.API_URL);
  url.searchParams.set('organization_id', organization_id);
  if (userId) url.searchParams.set('userId', userId);
  if (opts?.rangeDays) url.searchParams.set('rangeDays', String(opts.rangeDays));
  if (typeof opts?.onlyMine === 'boolean') url.searchParams.set('onlyMine', String(opts.onlyMine));
  const res = await fetch(url.toString(), {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || 'Failed to load dashboard');
  }
  return res.json();
}
